import { useEffect, useState } from "react";
import { LogIn } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";
import { useRouters } from "../context/RouterContext";
import { Card, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { RouterCombobox } from "../components/RouterCombobox";
import { FibiLoading } from "../components/FibiLoading";
import { LimitUptimeAudit } from "../components/LimitUptimeAudit";
import { VoucherTerpakaiPerCabang, MacCellRow } from "../components/VoucherTerpakaiPerCabang";
import { DatePicker } from "../components/DatePicker";

const today = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);

export default function VoucherTerpakai() {
  const { routers } = useRouters();
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [rid, setRid] = useState("ALL");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [onlyShared, setOnlyShared] = useState(false);
  const [scan, setScan] = useState(null);
  const loadScan = () => api.get("/vouchers/usage-scan/status").then((r) => setScan(r.data)).catch(() => {});
  useEffect(() => { loadScan(); const t = setInterval(loadScan, 15000); return () => clearInterval(t); }, []);
  const pindai = async () => {
    try {
      const { data } = await api.post("/vouchers/usage-scan");
      toast[data.already_running ? "info" : "success"](data.already_running ? "Pemindaian masih berjalan" : "Pemindaian dimulai — hasil masuk bertahap per 30 router");
      loadScan();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = () => {
    setLoading(true);
    api.get("/reports/voucher-terpakai", { params: { date_from: from, date_to: to, router_id: rid } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [from, to, rid]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = (data?.rows || []).filter((r) => (!onlyShared || r.shared) && (!q || r.name.toLowerCase().includes(q.toLowerCase()) || (r.profile || "").toLowerCase().includes(q.toLowerCase()) || (r.router_name || "").toLowerCase().includes(q.toLowerCase()) || (r.mac || "").toLowerCase().includes(q.toLowerCase())));

  const exportCsv = () => {
    const head = ["Waktu Login", "Cabang", "Kode", "Profile", "Mac Address", "Riwayat MAC", "Dipakai Bergantian", "Harga Jual", "Dicetak Oleh", "Batch"];
    const lines = rows.map((r) => [fmtDateTime(r.used_at), r.router_name, r.name, r.profile, r.mac || "", (r.macs || []).join(" | "), r.shared ? `YA (${r.macs.length} HP)` : "", r.jual, r.created_by, r.batch_id].map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(","));
    const blob = new Blob(["\uFEFF" + [head.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `voucher-terpakai_${from}_${to}.csv`; a.click();
  };

  return (
    <div className="space-y-5" data-testid="voucher-terpakai-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><LogIn className="w-6 h-6 text-emerald-500" /> Voucher Terpakai</h1>
          <p className="text-sm text-muted-foreground mt-1">Kode voucher yang sudah login di MikroTik (login pertama) — dipindai otomatis <b>tiap 1 jam</b> dari semua cabang; voucher yang masa berlakunya habis ikut dihapus dari router (pengaman bila router mati lampu/reboot). Pembatas utama tetap di router: limit-uptime & scheduler expired per kode.</p>
          {scan && (
            <div className="text-xs font-mono mt-1.5 flex items-center gap-2 flex-wrap" data-testid="vt-scan-status">
              {scan.status === "running" ? <span className="text-amber-400 animate-pulse">Memindai… {scan.progress ?? 0}/{scan.scanned ?? "?"} router, {scan.stamped ?? 0} login baru</span>
                : scan.status === "done" ? <span className="text-muted-foreground">Pindai terakhir {fmtDateTime(scan.finished_at)} — {scan.ok}/{scan.scanned} router terbaca, {scan.stamped} login baru{scan.expired_removed ? `, ${scan.expired_removed} voucher habis dihapus` : ""}</span>
                : <span className="text-rose-400">Belum pernah dipindai</span>}
              <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={pindai} disabled={scan.status === "running"} data-testid="vt-scan-now">Pindai sekarang</Button>
            </div>
          )}
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="w-52"><RouterCombobox routers={routers} value={rid} onChange={setRid} allLabel="Semua Cabang" testid="vt-router" /></div>
          <DatePicker value={from} onChange={(v) => setFrom(v)} className="w-40" testid="vt-from" />
          <DatePicker value={to} onChange={(v) => setTo(v)} className="w-40" testid="vt-to" />
          <Button variant="outline" onClick={exportCsv} disabled={!rows.length} data-testid="vt-export">Unduh CSV</Button>
        </div>
      </div>

      {data && (
        <div className="flex items-center gap-2 flex-wrap text-xs font-mono" data-testid="vt-summary">
          <span className="px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-500 font-bold" data-testid="vt-total">{data.total} kode login</span>
          <button type="button" onClick={() => setOnlyShared(!onlyShared)} className={`px-2.5 py-1.5 rounded-lg border font-bold ${data.shared_total ? "bg-rose-500/10 border-rose-500/30 text-rose-400" : "border-border text-muted-foreground"} ${onlyShared ? "ring-2 ring-rose-400" : ""}`} title="Kode yang MAC-nya berganti-ganti — indikasi 1 voucher dibagikan ke banyak HP. Klik untuk memfilter." data-testid="vt-shared-total">⚠ {data.shared_total || 0} dipakai bergantian</button>
          <span className="px-2.5 py-1.5 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-400" data-testid="vt-nilai">Nilai jual: {rupiah(data.total_nilai)}</span>
          {data.per_profile.slice(0, 8).map((p) => <span key={p.profile} className="px-2.5 py-1.5 rounded-lg bg-accent/60 border border-border" data-testid={`vt-prof-${p.profile}`}>{p.profile}: {p.qty}</span>)}
        </div>
      )}

      {data && rid === "ALL" && data.per_router.length > 1 && <VoucherTerpakaiPerCabang rows={rows} />}

      <LimitUptimeAudit routerId={rid === "ALL" ? "" : rid} />

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="vt-table-card">
        <CardHeader className="py-3 flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base font-heading">Rincian Login</CardTitle>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kode / profile / cabang" className="w-56 h-8 text-xs" data-testid="vt-search" />
        </CardHeader>
        <Table className="min-w-[720px]" data-testid="vt-table">
          <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Waktu Login</TableHead><TableHead>Cabang</TableHead><TableHead>Kode</TableHead><TableHead>Profile</TableHead><TableHead>Mac Address</TableHead><TableHead className="text-right">Harga</TableHead><TableHead>Dicetak Oleh</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={8} className="p-0"><FibiLoading text="FIBI sedang memuat data..." size={20} /></TableCell></TableRow>}
            {!loading && !rows.length && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground" data-testid="vt-empty">Tidak ada voucher login pada rentang ini</TableCell></TableRow>}
            {rows.map((r, i) => (
              <TableRow key={`${r.router_id}-${r.name}`} data-testid={`vt-row-${i}`}>
                <TableCell className="font-mono text-muted-foreground">{i + 1}</TableCell>
                <TableCell className="font-mono text-xs">{fmtDateTime(r.used_at)}{r.source === "scan" && <span className="ml-1 text-[9px] text-muted-foreground" title="Waktu dari pemindaian berkala (±30 menit), bukan log router">±30m</span>}</TableCell>
                <TableCell className="text-xs">{r.router_name}</TableCell>
                <TableCell className="font-mono font-semibold">{r.name}</TableCell>
                <TableCell className="text-xs">{r.profile}</TableCell>
                <TableCell className="font-mono text-xs text-sky-400 uppercase" data-testid={`vt-mac-${i}`}><MacCellRow r={r} /></TableCell>
                <TableCell className="text-right font-mono">{r.jual ? rupiah(r.jual) : "-"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.created_by || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
